import { parseSocketPayload } from "../hooks/wss/parseFrame";

const globalRevalidationGate = new Map<string, number>();

/** Decodes a realtime frame (already parsed by the transport, or a JSON string); never throws. */
export const parseRealtimeMessage = (messageData: any): any =>
  parseSocketPayload(messageData, { reportInvalid: false });

const isEvmAddress = (value?: string) => /^0x[a-fA-F0-9]{40}$/.test(value || "");

export const normalizeRealtimeChannel = (channel?: unknown) =>
  String(channel || "").split(":")[0].toLowerCase() || undefined;

export const getRealtimeAccount = (parsed: any) =>
  String(
    parsed?.account ||
      parsed?.data?.account ||
      parsed?.data?.order?.user ||
      String(parsed?.channel || "")
        .split(":")
        .find(isEvmAddress) ||
      "",
  ).toLowerCase() || undefined;

const stableOrderSignature = (order: any) =>
  [
    order?.order_id || order?.orderId || order?.id,
    order?.order_status || order?.status,
    order?.filled,
    order?.amount || order?.quantity,
    order?.price,
  ].join(":");

const stablePositionSignature = (position: any) =>
  [
    position?.position_key || position?.positionKey || position?.id,
    position?.instrument_id || position?.market,
    position?.amount || position?.sizeInTokens,
    position?.side || position?.isLong,
    position?.margin_type || position?.marginMode,
    position?.collateralAmount || position?.isolated_margin,
  ].join(":");

export const getRealtimeSnapshotItems = (parsed: any, channel?: string) => {
  const normalizedChannel = normalizeRealtimeChannel(channel);
  const data = parsed?.data;

  if (normalizedChannel === "orders") {
    return Array.isArray(data) ? undefined : data?.orders || data?.open_orders;
  }

  if (normalizedChannel === "positions") {
    return Array.isArray(data) ? data : data?.positions;
  }

  return undefined;
};

export const getRealtimeSnapshotSignature = (
  parsed: any,
  channel?: string
) => {
  const normalizedChannel = normalizeRealtimeChannel(channel);
  const orders = getRealtimeSnapshotItems(parsed, "orders");
  const positions = getRealtimeSnapshotItems(parsed, "positions");

  if (normalizedChannel === "orders" && Array.isArray(orders)) {
    return orders.map(stableOrderSignature).sort().join("|");
  }

  if (normalizedChannel === "positions" && Array.isArray(positions)) {
    return positions.map(stablePositionSignature).sort().join("|");
  }

  return undefined;
};

export const getRealtimeEventId = (parsed: any, channel?: string) => {
  const data = parsed?.data;
  const order = Array.isArray(data) ? undefined : data?.order;
  return [
    channel,
    data?.action,
    order?.orderId || order?.order_id || order?.id,
    order?.transactionId || order?.transaction_id,
    order?.status || order?.order_status,
    order?.timestamp,
    parsed?.timestamp,
  ].join(":");
};

export const isMeaningfulAccountRealtimeEvent = (
  parsed: any,
  channel?: string
) => {
  const normalizedChannel = normalizeRealtimeChannel(channel);
  const data = parsed?.data;
  if (Array.isArray(data)) return false;
  if (getRealtimeSnapshotSignature(parsed, normalizedChannel) !== undefined) {
    return false;
  }

  const action = String(data?.action || "").toLowerCase();
  const status = String(data?.order?.status || data?.order?.order_status || "")
    .toUpperCase();
  const meaningfulActions = new Set([
    "create",
    "created",
    "place",
    "placed",
    "cancel",
    "cancelled",
    "fill",
    "filled",
    "match",
    "opened",
    "increased",
    "decreased",
    "updated",
    "closed",
    "open",
    "close",
    "liquidate",
    "liquidated",
    "position_opened",
    "position_updated",
    "position_closed",
  ]);
  const meaningfulStatuses = new Set([
    "OPEN",
    "FILLED",
    "PARTIALLY_FILLED",
    "CANCELLED",
    "EXPIRED",
    "REJECTED",
    "FAILED",
  ]);

  return (
    normalizedChannel === "orders" ||
    normalizedChannel === "positions"
  ) && (meaningfulActions.has(action) || meaningfulStatuses.has(status));
};

export const shouldRunRealtimeRevalidation = (
  key: string,
  throttleMs: number
) => {
  const now = Date.now();
  const lastRun = globalRevalidationGate.get(key) || 0;
  if (now - lastRun < throttleMs) return false;
  globalRevalidationGate.set(key, now);
  return true;
};
